import { Node, mergeAttributes } from '@tiptap/core';

/**
 * ThreeCirclesWalkthrough Extension
 * Placeholder node for the interactive 3 Circles gospel-story walkthrough.
 * The actual interactive SVG is rendered at view time by
 * public/js/three-circles-walkthrough.js (initialized from course-view.html);
 * in the editor this renders as a static placeholder card.
 *
 * Attrs:
 *  - id: unique block id (stage progress saved as `${id}-stage` in lesson_responses)
 *  - gateIds: comma-separated textAreaBlock ids that stay hidden until the
 *    walkthrough reaches its final stage
 */
export const ThreeCirclesWalkthrough = Node.create({
  name: 'threeCirclesWalkthrough',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-id'),
        renderHTML: (attributes) => ({ 'data-id': attributes.id }),
      },
      gateIds: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-gate-ids') || '',
        renderHTML: (attributes) => ({ 'data-gate-ids': attributes.gateIds || '' }),
      },
      // 'walkthrough' (default): staged build, persists progress, gates gateIds.
      // 'review': opens fully built for revisiting — no persistence, no gating.
      mode: {
        default: 'walkthrough',
        parseHTML: (element) => element.getAttribute('data-mode') || 'walkthrough',
        renderHTML: (attributes) => ({ 'data-mode': attributes.mode || 'walkthrough' }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="three-circles-walkthrough"]' }];
  },

  renderHTML({ node, HTMLAttributes }) {
    const id = node.attrs.id || HTMLAttributes['data-id'] || 'three-circles';

    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'three-circles-walkthrough',
        'data-id': id,
        'data-gate-ids': node.attrs.gateIds || '',
        class: 'three-circles-block',
        style: 'margin: 2rem 0;',
      }),
      [
        'div',
        {
          class: 'three-circles-placeholder',
          style:
            'border: 2px dashed #2B6B5F; border-radius: 12px; padding: 2rem; text-align: center; color: #2B6B5F; background: #f0fdfa;',
        },
        [
          'div',
          { style: 'font-size: 1.5rem; margin-bottom: 0.5rem;' },
          '○ ○ ○', // three circles
        ],
        ['div', { style: 'font-weight: 700;' }, 'Interactive 3 Circles Walkthrough'],
        [
          'div',
          { style: 'font-size: 0.9rem; margin-top: 0.4rem;' },
          'Renders as a step-through gospel diagram on the course page.',
        ],
      ],
    ];
  },

  addCommands() {
    return {
      setThreeCirclesWalkthrough:
        (attributes) =>
        ({ commands }) => {
          const id = `three-circles-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          return commands.insertContent({
            type: this.name,
            attrs: { id, ...attributes },
          });
        },
    };
  },
});

export default ThreeCirclesWalkthrough;
